/**
 * Zoom Phone setup tools shown under a Zoom provider row (TRUS-652).
 *
 * - The provider's own webhook URLs, which IFR's Zoom admin pastes into the
 *   Zoom app's event subscriptions. Each Zoom provider has private URLs so its
 *   Secret Token can be checked before the payload is trusted.
 * - "Check Zoom numbers": asks Zoom which numbers the account holds and whether
 *   each can send texts (the cause of Zoom error 7642 when it can't).
 * - On SMS rows, staff members' own Zoom numbers, sent through this provider.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check, Loader2, Phone, Trash2, UserPlus, AlertCircle } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface ZoomProvider {
  id: string;
  channel: string;
  phone_number?: string;
}

interface ZoomNumber {
  phone_number: string;
  assignee?: string | null;
  capability?: string[];
  sms_enabled: boolean;
}

interface Assignment {
  id: string;
  user_id: number;
  user_name: string;
  phone_number: string;
}

interface StaffOption {
  user_id: number;
  name: string;
}

function absoluteUrl(path: string): string {
  const url = getWeaveinApiUrl(path);
  if (/^https?:\/\//.test(url)) return url;
  return typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-xs text-gray-500 dark:text-gray-400 w-28 shrink-0">{label}</span>
      <code className="flex-1 min-w-0 truncate text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-gray-800 dark:text-gray-200">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        title="Copy"
        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
      >
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

async function readError(res: Response, fallback: string): Promise<string> {
  const data = await res.json().catch(() => ({}));
  return typeof data?.detail === 'string' ? data.detail : fallback;
}

export function ZoomProviderTools({ provider }: { provider: ZoomProvider }) {
  const base = `/api/webhooks/zoom/p/${provider.id}`;
  const urls =
    provider.channel === 'voice'
      ? [{ label: 'Call events', value: absoluteUrl(`${base}/voice/events`) }]
      : [
          { label: 'Incoming texts', value: absoluteUrl(`${base}/sms/incoming`) },
          { label: 'Delivery status', value: absoluteUrl(`${base}/sms/status`) },
        ];

  // --- Check Zoom numbers ---
  const [checking, setChecking] = useState(false);
  const [numbers, setNumbers] = useState<ZoomNumber[] | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  const checkNumbers = async () => {
    setChecking(true);
    setCheckError(null);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/communication-providers/${provider.id}/zoom-numbers`));
      if (!res.ok) throw new Error(await readError(res, 'Could not reach Zoom'));
      const data = await res.json();
      if (data.status !== 'ok') throw new Error(data.error || 'Zoom did not return the account numbers');
      setNumbers(data.numbers || []);
    } catch (e: any) {
      setNumbers(null);
      setCheckError(e.message);
    } finally {
      setChecking(false);
    }
  };

  // --- Staff numbers (SMS rows only) ---
  const isSms = provider.channel === 'sms';
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [perUserEnabled, setPerUserEnabled] = useState(true);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [newUserId, setNewUserId] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStaff = useCallback(async () => {
    if (!isSms) return;
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/communication-providers/${provider.id}/user-numbers`));
      if (!res.ok) throw new Error(await readError(res, 'Could not load staff numbers'));
      const data = await res.json();
      setAssignments(data.assignments || []);
      setStaff(data.staff || []);
      setPerUserEnabled(Boolean(data.per_user_numbers_enabled));
      setStaffError(null);
    } catch (e: any) {
      setStaffError(e.message);
    }
  }, [isSms, provider.id]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const assign = async () => {
    if (!newUserId || !newNumber.trim()) return;
    setSaving(true);
    setStaffError(null);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/communication-providers/${provider.id}/user-numbers`), {
        method: 'POST',
        jsonBody: { user_id: Number(newUserId), phone_number: newNumber.trim() },
      });
      if (!res.ok) throw new Error(await readError(res, 'Could not assign the number'));
      setNewUserId('');
      setNewNumber('');
      await loadStaff();
    } catch (e: any) {
      setStaffError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: Assignment) => {
    setStaffError(null);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/user-phone-numbers/${a.id}/deactivate`), { method: 'POST' });
      if (!res.ok) throw new Error(await readError(res, 'Could not remove the number'));
      await loadStaff();
    } catch (e: any) {
      setStaffError(e.message);
    }
  };

  return (
    <div className="mt-2 ml-1 pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-3">
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
          Zoom webhook URLs <span className="font-normal text-gray-400">(paste into the Zoom app's event subscriptions)</span>
        </p>
        {urls.map(u => (
          <CopyField key={u.label} label={u.label} value={u.value} />
        ))}
      </div>

      <div>
        <button
          type="button"
          onClick={checkNumbers}
          disabled={checking}
          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 disabled:opacity-60"
        >
          {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />}
          Check Zoom numbers
        </button>
        {checkError && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" /> {checkError}
          </p>
        )}
        {numbers && (
          <ul className="mt-2 space-y-1">
            {numbers.length === 0 && <li className="text-xs text-gray-500">Zoom returned no numbers for this account.</li>}
            {numbers.map(n => (
              <li key={n.phone_number} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <span className="font-mono">{n.phone_number}</span>
                {n.assignee && <span className="text-gray-500">{n.assignee}</span>}
                {n.sms_enabled ? (
                  <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
                    Can text
                  </span>
                ) : (
                  <span className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">
                    Voice only. Turn on Messaging and link it to the 10DLC campaign in Zoom
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isSms && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Staff Zoom numbers</p>
          {!perUserEnabled && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              These take effect once Phone Allocation Mode is set to Per user and saved below.
            </p>
          )}
          {assignments.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              No staff numbers yet. Staff without one text from {provider.phone_number || 'this provider’s number'}.
            </p>
          ) : (
            <ul className="space-y-1">
              {assignments.map(a => (
                <li key={a.id} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                  <span className="min-w-0 truncate">{a.user_name}</span>
                  <span className="font-mono">{a.phone_number}</span>
                  <button
                    type="button"
                    onClick={() => remove(a)}
                    title="Remove this number"
                    className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <select
              id={`zoom-staff-${provider.id}`}
              value={newUserId}
              onChange={e => setNewUserId(e.target.value)}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Choose a staff member</option>
              {staff.map(s => (
                <option key={s.user_id} value={s.user_id}>
                  {s.name}
                </option>
              ))}
            </select>
            <input
              id={`zoom-number-${provider.id}`}
              value={newNumber}
              onChange={e => setNewNumber(e.target.value)}
              placeholder="Their Zoom number, +14155550123"
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-56"
            />
            <button
              type="button"
              onClick={assign}
              disabled={saving || !newUserId || !newNumber.trim()}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              Assign
            </button>
          </div>
          {staffError && (
            <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" /> {staffError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ZoomProviderTools;
